import {useMemo} from 'react';
import {AnalysisItem, KodataCategoryGroup, SalesCompare as ISalesCompare} from '@/map/@types';
import {useCompareSalesByPoint, useCompareSalesByPolygon} from '@/map/api';
import {AreaOverlayType} from '@/map/enums';
import {ErrorItem} from '../../ErrorItem';
import {SectionBlock} from '../../StyledComponents';
import {HallSalesCompare} from './HallSales';
import {SalesAgeGenderCompare} from './SalesAgeGender';
import {SalesTimeCompare} from './SalesTime';
import {SalesWeekWeekendCompare} from './SalesWeekWeekend';
import {StoreCountCompare} from './StoreCount';
import {TotalStoreCountTrend} from './TotalStoreCountTrend';

interface IProps {
  category: KodataCategoryGroup;
  list: AnalysisItem[];
}

export const SalesCompare = ({list, category}: IProps) => {
  const circles = useMemo(() => {
    return list.filter((i) => i.type === AreaOverlayType.CIRCLE);
  }, [list]);
  const polygons = useMemo(() => {
    return list.filter((i) => i.type === AreaOverlayType.POLYGON);
  }, [list]);

  const {queries: circleResults, refetchAll: refetchAllCircles} =
    useCompareSalesByPoint(circles, category);
  const {queries: polygonResults, refetchAll: refetchAllPolygons} =
    useCompareSalesByPolygon(polygons, category);

  const combinedResults = useMemo(() => {
    const results = [...(circleResults || {}), ...(polygonResults || {})];

    return list.map((i) => {
      const index =
        i.type === AreaOverlayType.CIRCLE
          ? circles.findIndex((j) => j.id === i.id)
          : polygons.findIndex((j) => j.id === i.id) + circles?.length;

      return results[index];
    });
  }, [circleResults, circles, list, polygonResults, polygons]);

  const combined = useMemo(() => {
    const data = combinedResults.map((result) => result?.data);

    return {
      data: (data.some((i) => !i) ? undefined : data) as
        | ISalesCompare[]
        | undefined,
      isPending: combinedResults.some((result) => result.isLoading),
      error: combinedResults
        .map((result) => result.error)
        .filter((x) => !!x)?.[0],
    };
  }, [combinedResults]);

  const refetchAll = () => {
    refetchAllCircles();
    refetchAllPolygons();
  };

  if (combined.error) {
    return (
      <SectionBlock>
        <ErrorItem onRetry={refetchAll} />
      </SectionBlock>
    );
  }

  return (
    <>
      <StoreCountCompare {...combined} />
      <TotalStoreCountTrend {...combined} />
      <HallSalesCompare {...combined} />
      <SalesAgeGenderCompare {...combined} />
      <SalesWeekWeekendCompare {...combined} />
      <SalesTimeCompare {...combined} />
    </>
  );
};
